/**
 * Scroll restoration for a shell that scrolls a `div`, not the window.
 *
 * TanStack Router snapshots every element that scrolls and, on a *new*
 * navigation, carries the previous location's offsets forward to the new one —
 * its unconditional "scroll to top" fallback only ever targets `window`, which
 * never scrolls in this app. Without the opt-out below, opening a product from
 * halfway down the catalog lands you halfway down the product page.
 *
 * Listing a container here restores the expected pair of behaviours:
 * a forward navigation starts it at the top, while a history pop still restores
 * the position that entry was left at (pops reuse the entry's own snapshot).
 *
 * Ids are explicit because the library otherwise keys on a positional
 * `div:nth-child(2) > …` selector, which changes whenever shell markup does and
 * can collide between two shells with similar structure.
 */

export const SCROLL_RESTORATION_ATTRIBUTE = "data-scroll-restoration-id";

/** The scrolling content column of `AppShell` / `DashboardShell`. */
export const APP_SCROLL_ID = "app-content";
export const DASHBOARD_SCROLL_ID = "dashboard-content";

/**
 * Horizontal rails on the marketplace discovery view. Each is listed
 * individually: a `scrollToTopSelectors` entry resolves a single element, so a
 * shared prefix selector would only ever reset the first rail.
 *
 * Rails are reset-only. Restoring a rail's page on a back navigation needs the
 * offset applied after its products have laid out, which races the router's own
 * restore pass; every arrival therefore starts at the first card.
 */
export const MARKETPLACE_RAIL_SECTION_IDS = {
  trending: "marketplace-trending",
  newReleases: "marketplace-new-releases",
  featuredStores: "marketplace-featured-stores",
  freeAssets: "marketplace-free-assets",
} as const;

export function railScrollId(sectionId: string) {
  return `rail-${sectionId}`;
}

export function scrollRestorationSelector(id: string) {
  return `[${SCROLL_RESTORATION_ATTRIBUTE}="${id}"]`;
}

/** Passed to the router as `scrollToTopSelectors`. Selectors that match nothing are skipped. */
export const SCROLL_TO_TOP_SELECTORS: string[] = [
  APP_SCROLL_ID,
  DASHBOARD_SCROLL_ID,
  ...Object.values(MARKETPLACE_RAIL_SECTION_IDS).map(railScrollId),
].map(scrollRestorationSelector);
